/**
 * Sintetizador Web Audio API para notificaciones sonoras en tablets/POS.
 * Genera un timbre / campana cristalina agradable sin depender de archivos de audio externos.
 */

let audioCtx: AudioContext | null = null;

function obtenerAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioCtxClass) {
      audioCtx = new AudioCtxClass();
    }
  }
  if (audioCtx && audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
}

/**
 * Emite un timbre cristalino agudo de campana doble ("Ding-Dong-Ding") para notificar llamadas de mesero QR.
 */
export function reproducirSonidoNotificacionQr() {
  try {
    const ctx = obtenerAudioContext();
    if (!ctx) return;

    const ahora = ctx.currentTime;

    // NOTA 1: Tono agudo (Mi6 - 1318.5 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(1318.51, ahora);
    gain1.gain.setValueAtTime(0.35, ahora);
    gain1.gain.exponentialRampToValueAtTime(0.001, ahora + 0.5);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(ahora);
    osc1.stop(ahora + 0.5);

    // NOTA 2: Tono cristalino armónico (La6 - 1760 Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(1760, ahora + 0.12);
    gain2.gain.setValueAtTime(0.45, ahora + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.001, ahora + 0.7);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ahora + 0.12);
    osc2.stop(ahora + 0.7);

    // NOTA 3: Resonancia de campana final (Do7 - 2093 Hz)
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = "sine";
    osc3.frequency.setValueAtTime(2093, ahora + 0.28);
    gain3.gain.setValueAtTime(0.5, ahora + 0.28);
    gain3.gain.exponentialRampToValueAtTime(0.001, ahora + 1.1);
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    osc3.start(ahora + 0.28);
    osc3.stop(ahora + 1.1);

    // VIBRACIÓN HÁPTICA EN CELULARES/TABLETS (45ms + 80ms + 120ms)
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate([50, 80, 80, 80, 120]);
    }
  } catch (err) {
    console.warn("No se pudo reproducir el sonido QR:", err);
  }
}

/**
 * Emite un tono rítmico alegre ("Titi-Ring") para notificar nuevos domicilios online.
 */
export function reproducirSonidoDomicilio() {
  try {
    const ctx = obtenerAudioContext();
    if (!ctx) return;

    const ahora = ctx.currentTime;
    const freqs = [1046.5, 1318.5, 1567.98]; // C6, E6, G6 (Acorde alegre)

    freqs.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ahora + idx * 0.1;
      osc.type = "triangle";
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.35, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
    });

    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate([60, 60, 60]);
    }
  } catch (err) {
    console.warn("No se pudo reproducir sonido domicilio:", err);
  }
}

/**
 * Emite un tono festivo de caja registradora / dinero ("Ka-Ching") para notificar transferencias bancarias recibidas.
 */
export function reproducirSonidoPagoBancario() {
  try {
    const ctx = obtenerAudioContext();
    if (!ctx) return;

    const ahora = ctx.currentTime;
    const freqs = [1567.98, 2093.0]; // G6 -> C7

    freqs.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ahora + idx * 0.12;
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.5, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.6);
    });

    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate([100, 50, 100]);
    }
  } catch (err) {
    console.warn("No se pudo reproducir sonido pago bancario:", err);
  }
}

/**
 * Anuncia por voz alta (Web Speech API) la transferencia bancaria recibida.
 * Ejemplo: "¡Pago recibido por Nequi! 45.000 pesos de Carlos Gómez."
 */
export function reproducirVozPagoBancario(
  monto: number | string,
  banco: string,
  remitente?: string | null,
) {
  try {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;

    const numMonto = Number(monto ?? 0);
    const montoFormateado = new Intl.NumberFormat("es-CO", {
      style: "decimal",
      maximumFractionDigits: 0,
    }).format(numMonto);

    const remitenteTexto = remitente ? ` de ${remitente}` : "";
    const mensaje = `¡Pago recibido por ${banco}! ${montoFormateado} pesos${remitenteTexto}.`;

    // Cancelar cualquier mensaje en curso
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(mensaje);
    utterance.lang = "es-CO";
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    const voces = window.speechSynthesis.getVoices();
    const vozEspanol = voces.find(
      (v) => v.lang.startsWith("es-CO") || v.lang.startsWith("es-MX") || v.lang.startsWith("es"),
    );
    if (vozEspanol) {
      utterance.voice = vozEspanol;
    }

    window.speechSynthesis.speak(utterance);
  } catch (err) {
    console.warn("No se pudo reproducir voz de pago bancario:", err);
  }
}

export function reproducirSonidoNotificacion() {
  reproducirSonidoNotificacionQr();
}
